import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal } from '@rtkwlf/ui';
import { VirtualLogCollector } from '../../../types';
import { useTrashablePromise } from '../../../hooks';
import { apiRawFetch, apiRequest } from '../../../APIClient';

export const useDecommissionSensor = (getVirtualLogCollectors: () => void) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [vlcToDelete, setVlcToDelete] =
    React.useState<VirtualLogCollector | null>(null);

  const closeModal = React.useCallback(() => {
    setVlcToDelete(null);
  }, []);

  const onConfirmDelete = React.useCallback((vlc: VirtualLogCollector) => {
    setVlcToDelete(vlc);
  }, []);

  const { registerPromise } = useTrashablePromise();
  const dispatch = useDispatch();

  const onDelete = React.useCallback(async () => {
    if (!vlcToDelete) {
      return;
    }

    try {
      setIsLoading(true);
      await registerPromise(apiRawFetch.decommissionLogCollector(vlcToDelete));
      getVirtualLogCollectors();
      dispatch(
        apiRequest.setGlobalStatus(
          `${
            vlcToDelete.is_vanilla ? 'Physical appliance' : 'Virtual appliance'
          } ${vlcToDelete.serial_number} successfully decommissioned.`,
          'success',
        ),
      );
    } catch (err) {
      dispatch(apiRequest.setGlobalStatus(err.message, err.color || 'danger'));
    } finally {
      setVlcToDelete(null);
      setIsLoading(false);
    }
  }, [vlcToDelete, dispatch, registerPromise, getVirtualLogCollectors]);

  const modal: React.ReactNode = React.useMemo(() => {
    if (!vlcToDelete) {
      return null;
    }

    const type =
      vlcToDelete?.product_type?.toLowerCase() === 'vsensor'
        ? 'virtual sensor'
        : vlcToDelete?.product_type?.toLowerCase() === 'vlc'
        ? 'virtual log collector'
        : vlcToDelete?.is_vanilla
        ? 'physical appliance'
        : 'virtual appliance';

    return (
      <Modal
        visible
        title={
          <span>
            Decommission {vlcToDelete.is_vanilla ? 'Physical' : 'Virtual'}{' '}
            Appliance
          </span>
        }
        onCancel={closeModal}
        onOk={onDelete}
        footer={
          <>
            <Button
              data-cy="cancel-remove"
              type="primary"
              ghost
              disabled={isLoading}
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              data-cy="confirm-remove"
              onClick={onDelete}
              disabled={isLoading}
            >
              <span>
                Decommission {vlcToDelete.is_vanilla ? 'Physical' : 'Virtual'}{' '}
                Appliance
              </span>
            </Button>
          </>
        }
      >
        <div style={{ maxWidth: 600 }}>
          <p>
            {`Are you sure you want to decommission the ${type} ${
              vlcToDelete.serial_number
            }${vlcToDelete.short_name ? ` (${vlcToDelete.short_name})` : ''}?`}
          </p>
          {vlcToDelete?.product_type?.toLowerCase() === 'scanner' ? (
            <p>
              Please note that if you remove this device, any scan schedules and
              deny lists associated with the device will be lost. If you'd like
              a copy of the setting(s), please contact your CST to collect this
              information before proceeding with removing the device. Once
              deleted, the settings are not recoverable.
            </p>
          ) : (
            <p>
              This action <strong>permanently decommissions this {type}</strong>{' '}
              and removes it from the list. You must manually remove the {type}{' '}
              from the hypervisor that it is installed on.
            </p>
          )}
        </div>
      </Modal>
    );
  }, [vlcToDelete, closeModal, onDelete, isLoading]);

  return {
    modal,
    onConfirmDelete,
  };
};
